import { MutableRefObject, useRef, useLayoutEffect } from 'react';
import { shallow } from 'zustand/shallow';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { documentationFilteredFileResetDialog } from 'components/documentation/documentationFilteredFileResetDialog';
import { DocumentState } from 'components/common/dataGrid/columnsFactory/types';
import { pollUntil } from 'shared/utils/pollUntil';
import { createCleanupCollector } from 'hooks/createStore';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { useDocumentationStore } from '../../store';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import {
  findMatchingFileIndex,
  shouldDelayMissingDecision,
} from './model';
import type { DocumentationNavigationFile, FileOpenContext } from './types';

const LOADING_POLL_INTERVAL_MS = 100;
const URL_FILE_OPEN_TIMEOUT_MS = 15_000;

/**
 * Opens the file from a `/documentation/file/:fileId` URL: navigates to its
 * directory, waits for the list, asks to reset filters when the file is
 * filtered out, then opens the gallery on it. Returns the ref of the file still
 * waiting to be opened (shared with the gallery URL synchronization).
 */
export function useOpenDocumentationFileFromUrl(
  clearFilterAndSortModel: (state: DocumentState) => void,
  isDocumentationListLoading: () => boolean,
  onDialogVisibilityChange: (isOpen: boolean) => void
): MutableRefObject<string | undefined> {
  const navigateToDirectory = useDocumentationStore.use.navigateToDirectory();
  const fetchDirectoryContent =
    useDocumentationStore.use.fetchDirectoryContent();
  const setGalleryState = useDocumentationStore.use.setGalleryState();
  const setUrlFileOpenPending =
    useDocumentationStore.use.setUrlFileOpenPending();

  const fileId = useAppUrlParams(UrlParamName.FILE_ID);
  const fileToOpenRef = useRef<string | undefined>(fileId);
  const fileIdRef = useRef(fileId);
  fileIdRef.current = fileId;

  const createDialog = useDialog();

  const dialogWasShownRef = useRef(false);
  const lastFileIdRef = useRef<string | undefined>(undefined);

  // useLayoutEffect (not useEffect): on the very first render of a direct
  // "/documentation/file/:fileId" URL load, `urlFileOpenPending` is still
  // `false` (its initial store value), so the grid-masking flag in
  // `DocumentationDataGrid` would be `false` too and the table would flash
  // for one frame before this effect flips it to `true`. Running this in
  // the layout phase lets the resulting `setUrlFileOpenPending(true)` state
  // update flush before the browser paints, eliminating the flash.
  useLayoutEffect(() => {
    // Reset dialogWasShown only when fileId changes (new navigation)
    if (lastFileIdRef.current !== fileId) {
      dialogWasShownRef.current = false;
      lastFileIdRef.current = fileId;
    }

    const cleanups = createCleanupCollector();
    let isEffectActive = true;
    // Read on demand - subscribing to files/directory would re-render the grid.
    const getFiles = (): DocumentationNavigationFile[] =>
      useDocumentationStore.getState().files;
    const getCurrentDirectory = (): string =>
      useDocumentationStore.getState().currentDirectory;

    const isCurrentDirectoryMatching = (parentDirectoryId: string): boolean =>
      areDirectoryIdsEqual(getCurrentDirectory(), parentDirectoryId);

    const isDataReady = (parentDirectoryId: string): boolean =>
      isCurrentDirectoryMatching(parentDirectoryId) && !isDocumentationListLoading();

    const clearUrlFileOpenAttempt = (): void => {
      fileToOpenRef.current = undefined;
      setUrlFileOpenPending(false);
    };

    const openGalleryAtIndex = (index: number): void => {
      clearUrlFileOpenAttempt();
      setGalleryState({ open: true, slide: index });
    };

    const tryOpenFileDirectly = (
      openRequest: FileOpenContext
    ): boolean => {
      const fileIndex = findMatchingFileIndex(getFiles(), openRequest);
      if (fileIndex === -1) {
        return false;
      }

      cleanups.cleanup();
      openGalleryAtIndex(fileIndex);
      return true;
    };

    const openFilterResetDialog = async (
      openRequest: FileOpenContext
    ): Promise<void> => {
      dialogWasShownRef.current = true;
      cleanups.cleanup();
      onDialogVisibilityChange(true);

      try {
        await createDialog(documentationFilteredFileResetDialog());

        const newState = openRequest.isArchived
          ? DocumentState.archived
          : DocumentState.published;
        clearFilterAndSortModel(newState);

        if (!isCurrentDirectoryMatching(openRequest.parentDirectoryId)) {
          void navigateToDirectory(openRequest.parentDirectoryId);
        }

        // Open the file as soon as it appears in the list after the reset.
        // Registered in `cleanups`, so leaving the URL cancels a pending open.
        const unsubscribe = useDocumentationStore.subscribe(
          (state) =>
            state.files.findIndex(
              (file) => file.documentationId === openRequest.documentationId
            ),
          (index) => {
            if (index !== -1) {
              unsubscribe();
              openGalleryAtIndex(index);
            }
          }
        );
        cleanups.add(unsubscribe);
      } catch {
        clearUrlFileOpenAttempt();
      } finally {
        onDialogVisibilityChange(false);
      }
    };

    const openOrRequestFilterReset = (
      openRequest: FileOpenContext
    ): void => {
      if (!isEffectActive || dialogWasShownRef.current) {
        return;
      }

      const fileIndex = findMatchingFileIndex(getFiles(), openRequest);

      if (fileIndex !== -1) {
        cleanups.cleanup();
        openGalleryAtIndex(fileIndex);
      } else {
        void openFilterResetDialog(openRequest);
      }
    };

    const watchUntilFileCanBeOpened = (
      openRequest: FileOpenContext
    ): void => {
      let hasObservedLoading = false;
      let hasObservedFilesChange = false;

      const tryResolveOpenRequest = (): void => {
        const isLoading = isDocumentationListLoading();
        if (isLoading) {
          hasObservedLoading = true;
          return;
        }

        if (!isCurrentDirectoryMatching(openRequest.parentDirectoryId)) {
          return;
        }

        const fileIndex = findMatchingFileIndex(getFiles(), openRequest);

        if (fileIndex !== -1) {
          openOrRequestFilterReset(openRequest);
          return;
        }

        const shouldDelay = shouldDelayMissingDecision(
          openRequest,
          hasObservedLoading,
          hasObservedFilesChange
        );

        if (shouldDelay) {
          return;
        }

        openOrRequestFilterReset(openRequest);
      };

      cleanups.add(
        useDocumentationStore.subscribe(
          (state) => ({ files: state.files, currentDirectory: state.currentDirectory }),
          ({ files }, previous) => {
            if (files !== previous.files) {
              hasObservedFilesChange = true;
            }
            tryResolveOpenRequest();
          },
          { equalityFn: shallow }
        )
      );

      cleanups.add(
        pollUntil({
          condition: () => {
            const loading = isDocumentationListLoading();
            if (loading) {
              hasObservedLoading = true;
            }
            return !loading;
          },
          onConditionMet: tryResolveOpenRequest,
          intervalMs: LOADING_POLL_INTERVAL_MS,
        })
      );

      // Check immediately in case data is already ready (directory may have been
      // navigated synchronously before subscription was added)
      if (isCurrentDirectoryMatching(openRequest.parentDirectoryId) && !isDocumentationListLoading()) {
        hasObservedLoading = true;
        tryResolveOpenRequest();
      }
    };

    const openDocumentFromUrl = async (documentId: string): Promise<void> => {
      const targetDoc = await documentationClient.getOneById(documentId);

      if (!isEffectActive) {
        return;
      }

      if (!targetDoc || documentId !== fileToOpenRef.current) {
        clearUrlFileOpenAttempt();
        return;
      }

      const parentDirectoryId =
        targetDoc.parentId == null ? ROOT_DIR_ID : targetDoc.parentId;

      const openRequest: FileOpenContext = {
        documentationId: targetDoc._id,
        parentDirectoryId,
        isArchived: Boolean(targetDoc.deleted),
        hasNavigatedToParentDirectory: false,
      };

      const shouldNavigateToParentDirectory = !isCurrentDirectoryMatching(
        openRequest.parentDirectoryId
      );

      if (shouldNavigateToParentDirectory) {
        openRequest.hasNavigatedToParentDirectory = true;
        navigateToDirectory(openRequest.parentDirectoryId);
      } else if (isDocumentationListLoading()) {
        fetchDirectoryContent(openRequest.parentDirectoryId);
      }

      if (isDataReady(openRequest.parentDirectoryId)) {
        if (tryOpenFileDirectly(openRequest)) {
          return;
        }
        void openFilterResetDialog(openRequest);
        return;
      }

      watchUntilFileCanBeOpened(openRequest);
    };

    if (fileId) {
      const state = useDocumentationStore.getState();
      const openedFile =
        state.galleryOpen && state.currentSlide >= 0
          ? state.files[state.currentSlide]
          : undefined;

      if (openedFile?.documentationId === fileId) {
        clearUrlFileOpenAttempt();
        return;
      }

      fileToOpenRef.current = fileId;
      setUrlFileOpenPending(true);

      const timeoutId = window.setTimeout(() => {
        if (fileToOpenRef.current === fileId) {
          clearUrlFileOpenAttempt();
        }
      }, URL_FILE_OPEN_TIMEOUT_MS);
      cleanups.add(() => window.clearTimeout(timeoutId));

      void openDocumentFromUrl(fileId);
    } else {
      setUrlFileOpenPending(false);
    }

    return () => {
      isEffectActive = false;
      cleanups.cleanup();
      if (!fileIdRef.current) {
        setUrlFileOpenPending(false);
      }
    };
  }, [
    fileId,
    createDialog,
    clearFilterAndSortModel,
    isDocumentationListLoading,
    onDialogVisibilityChange,
    navigateToDirectory,
    fetchDirectoryContent,
    setGalleryState,
    setUrlFileOpenPending,
  ]);

  return fileToOpenRef;
}
